import { useRef } from 'react'
import { ChevronDown, ChevronUp, ChevronsDown, ChevronsUp } from 'lucide-react'
import { cn } from '@/utils/cn'

export interface ScrollNavProps {
  /** Ref to the scroll container that this nav controls. */
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
  className?: string
}

/**
 * Floating scroll-control buttons for the workspace.
 *
 * Pinned to the lower-left of the workspace. Each pair (up/down) supports:
 *   - single click: scroll by ±100% of the container's viewport height
 *   - double click: jump to the very top or very bottom
 *
 * Long-press (≥500ms) is treated the same as double-click for parity with
 * the spec's "double-click OR long-press" requirement.
 */
export const ScrollNav = ({ scrollRef, className }: ScrollNavProps) => {
  const longPressTimer = useRef<number | null>(null)
  const longPressFired = useRef(false)

  const scrollBy = (dir: 'up' | 'down') => {
    const el = scrollRef.current
    if (!el) return
    const delta = dir === 'down' ? el.clientHeight : -el.clientHeight
    el.scrollBy({ top: delta, behavior: 'smooth' })
  }

  const scrollEdge = (dir: 'up' | 'down') => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTo({
      top: dir === 'down' ? el.scrollHeight : 0,
      behavior: 'smooth',
    })
  }

  const handlePointerDown = (dir: 'up' | 'down') => {
    longPressFired.current = false
    longPressTimer.current = window.setTimeout(() => {
      longPressFired.current = true
      scrollEdge(dir)
    }, 500)
  }

  const cancelLongPress = () => {
    if (longPressTimer.current !== null) {
      window.clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }

  const handleClick = (dir: 'up' | 'down') => {
    // If a long-press already fired, the timer's handler did the edge
    // jump — don't double-act.
    if (longPressFired.current) {
      longPressFired.current = false
      return
    }
    scrollBy(dir)
  }

  const handleDoubleClick = (dir: 'up' | 'down') => {
    cancelLongPress()
    scrollEdge(dir)
  }

  const btnCls =
    'p-2 rounded-full bg-white/90 dark:bg-slate-800/90 border border-slate-200 dark:border-slate-700 shadow-md text-slate-700 dark:text-slate-200 hover:bg-white dark:hover:bg-slate-800 hover:text-light-icy-blue dark:hover:text-dark-muted-teal transition-colors backdrop-blur'

  return (
    <div
      className={cn(
        'fixed bottom-6 left-6 z-40 flex flex-col gap-2',
        className,
      )}
    >
      <button
        type="button"
        aria-label="Scroll up one screen — double-click or long-press to jump to top"
        title="Scroll up one screen — double-click or long-press to jump to top"
        className={btnCls}
        onClick={() => handleClick('up')}
        onDoubleClick={() => handleDoubleClick('up')}
        onPointerDown={() => handlePointerDown('up')}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
      >
        <ChevronUp className="w-5 h-5" />
        <ChevronsUp className="w-3 h-3 -mt-1 mx-auto opacity-60" />
      </button>
      <button
        type="button"
        aria-label="Scroll down one screen — double-click or long-press to jump to bottom"
        title="Scroll down one screen — double-click or long-press to jump to bottom"
        className={btnCls}
        onClick={() => handleClick('down')}
        onDoubleClick={() => handleDoubleClick('down')}
        onPointerDown={() => handlePointerDown('down')}
        onPointerUp={cancelLongPress}
        onPointerLeave={cancelLongPress}
      >
        <ChevronsDown className="w-3 h-3 mb-1 mx-auto opacity-60" />
        <ChevronDown className="w-5 h-5" />
      </button>
    </div>
  )
}

export default ScrollNav
